import { Select, Table, TableColumnsType } from 'antd';
import _ from 'lodash';
import moment from 'moment';
import QueryString from 'qs';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { getFaqs } from '../../../api/boardFaq.api';
import Button from '../../../components/Button';
import ErrorResponse from '../../../components/ErrorResponse';
import SimpleHeader from '../../../components/layout/SimpleHeader';
import Loader from '../../../components/Loader';
import Pagination from '../../../components/Pagination';
import useAuth from '../../../hooks/useAuth';
import useAxios from '../../../hooks/useAxios';
import config from '../../../lib/config';
import { getCommonCodeLabel } from '../../../lib/store';
import { IFaqItem, IGetFaqs } from '../../../models/boardFaq.model';
import { RootState } from '../../../store/rootReducer';

const HomepageFaq = () => {
  const { t, i18n } = useTranslation();
  const { writeAuthYn } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // BR0005 = FAQ 카테고리
  const BR0005 = commonCodes.find(
    (item) => item.commonCode === 'BR0005',
  )?.detailCode;
  const { data: res, isLoading, error, execute } = useAxios<IGetFaqs>();

  const [faqDiv, setFaqDiv] = useState('');
  const [title, setTitle] = useState('');
  const [page, setPage] = useState(1);
  const [param, setParam] = useState({});
  const [data, setData] = useState<IFaqItem[]>([]);

  useEffect(() => {
    execute(getFaqs());
  }, []);

  useEffect(() => {
    if (res && !isLoading) {
      const _param: any = {};
      let _data: IFaqItem[] = _.cloneDeep(res.data);

      if (query.page) {
        const _page = Number(query.page);
        setPage(_page);
        _param.page = _page;
      }
      if (query.faqDiv) {
        const _faqDiv = String(query.faqDiv);
        setFaqDiv(_faqDiv);
        _param.faqDiv = _faqDiv;
        _data = _data.filter((item) => item.faqDiv === _faqDiv);
      }
      if (query.title) {
        const _title = String(query.title);
        setTitle(_title);
        _param.title = _title;
        _data = _data.filter((item) => item.title.includes(_title));
      }

      setParam(_param);
      setData(_data);
    }
  }, [res, isLoading, location]);

  const onSearch = () => {
    const param: any = { page: 1 };
    if (faqDiv) param.faqDiv = faqDiv;
    if (title) param.title = title;

    history.push(`/homepage/faq?${QueryString.stringify(param)}`);
  };

  const columns: TableColumnsType<IFaqItem> = [
    {
      title: t('homepage.archive.column1'),
      dataIndex: 'faqCode',
      key: 'faqCode',
      width: '6%',
      sorter: (a, b) => a.faqCode - b.faqCode,
    },
    {
      title: t('homepage.education.column1'),
      dataIndex: 'faqDiv',
      key: 'faqDiv',
      width: '7%',
      sorter: (a, b) => a.faqDiv?.localeCompare(b.faqDiv),
      render: (value) => getCommonCodeLabel('BR0005', value, i18n.language),
    },
    {
      title: t('common.column.title'),
      dataIndex: 'title',
      key: 'title',
      sorter: (a, b) => a.title?.localeCompare(b.title),
      ellipsis: true,
      render: value => {
        return <div className="text-left ant-table-cell-ellipsis">{value}</div>
      }
    },
    {
      title: t('homepage.archive.column6'),
      dataIndex: 'regMemKorName',
      key: 'regMemKorName',
      width: '10%',
      sorter: (a, b) => a.regMemKorName?.localeCompare(b.regMemKorName),
    },
    {
      title: t('homepage.archive.column5'),
      dataIndex: 'regDate',
      key: 'regDate',
      width: '10%',
      sorter: (a, b) => a.regDate?.localeCompare(b.regDate),
      render: (value) => moment(value).format('YYYY.MM.DD'),
    },
    {
      title: t('homepage.archive.column7'),
      dataIndex: 'readCount',
      key: 'readCount',
      align: 'center',
      width: '7%',
      sorter: (a, b) => a.readCount - b.readCount,
    },
  ];

  return (
    <>
    <SimpleHeader
      iconFile="icon-homepage-big.svg"
      parentMenu={t('common.menu.homepage')}
      currentMenu={t('homepage.faq.title1')}
      onlyTitle={true}
    />	
    <div className="common-search">
      <div className="search-area">
        <ul className="search-bx">
          <li>
            <label>{t('homepage.education.column1')}</label>
            <div>
              <Select
                placeholder={t('homepage.education.column1')}
                value={faqDiv}
                onChange={(value) => setFaqDiv(value)}
              >
                <Select.Option value="">{t('common.text.all')}</Select.Option>
                {BR0005?.map((item) => (
                  <Select.Option
                    key={item.detailCode}
                    value={item.detailCode}
                  >
                    {i18n.language === 'ko' ? item.korName : item.engName}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </li>
          <li>
            <label>{t('common.column.title')}</label>
            <input
              type="text"
              className="form-control w-240p"
              placeholder={t('homepage.archive.placeholder1')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') onSearch();
              }}
            />
            <div className="comm-btn__area">
              <Button
                className="btn-basic small black"
                onClick={onSearch}
                text={t('common.button.search')}
              />
            </div>
          </li>
        </ul>
      </div>
    </div>
    <div className="content scrollbar">
      {isLoading ? <Loader /> : null}
      <ErrorResponse error={error} />

      <div className="col-frame">
        <div className="comm-bx__between">
          <div className="comm-sch_result">{t('common.text.count')}: <b>{data.length}</b>{t('common.text.unit')}</div>
          <div className="comm-btn__area">
            <Button
              className="btn-basic pink"
              onClick={() => history.push('/homepage/faq/create')}
              text={t('homepage.archive.button1')}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
        <div className='comm-table__area'>
          <Table
            columns={columns}
            dataSource={data}
            pagination={{
              current: page,
              pageSize: config.pageSize,
            }}
            onRow={(record) => {
              return {
                onClick: () => {
                  history.push(
                    `/homepage/faq/${record.faqCode}${location.search}`,
                  );
                },
              };
            }}
            rowClassName="pointer"
            rowKey="faqCode"
          />
        </div>
        <div className="comm-bx__between">
          <div />
          <div className="common-paging center">
            <Pagination
              count={data.length}
              page={page}
              param={param}
              path="/homepage/faq"
            />
          </div>
          <div className="comm-btn__area right">
            <Button
              className="btn-basic pink"
              onClick={() => history.push('/homepage/faq/create')}
              text={t('homepage.archive.button1')}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default HomepageFaq;
